import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, IndianRupee, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

import { useTranslation } from "react-i18next";
/**
 * DealerDailyBriefing — top-of-home greeting card that shows:
 *   - Greeting bucket + dealer's name
 *   - Yesterday's sales/shortage/credit at a glance
 *   - Top 3 AI anomalies (or rule-based fallback)
 *   - Today's expected sales (rolling weekday average)
 *
 * Silently hides itself if the backend errors, so it never breaks the home
 * page during a partial outage.
 */
export default function DealerDailyBriefing() {
  const { t } = useTranslation();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dealer/daily-briefing");
        setB(data);
      } catch { setErr(true); }
    })();
  }, []);
  if (err || !b) return null;
  const y = b.yesterday || {};
  const fc = b.today_forecast || {};
  const delta = (fc.sales_expected && y.sales)
    ? ((y.sales - fc.sales_expected) / fc.sales_expected) * 100
    : null;
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/10 via-background to-amber-500/[0.06] p-5 shadow-lg md:p-6"
      data-testid="dealer-daily-briefing"
    >
      <div className="pointer-events-none absolute -right-16 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
      <div className="pointer-events-none absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-amber-500/10 blur-2xl" />

      <div className="relative flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-bold">
          {b.greeting}, {b.dealer_name?.split(" ")[0] || "there"}!
        </h2>
      </div>

      <div className="relative mt-3 grid gap-4 md:grid-cols-3">
        <MiniStat
          icon={<IndianRupee className="h-4 w-4" />}
          label={t("components.dealerdailybriefing.yesterday_sales")}
          value={`₹${(y.sales || 0).toLocaleString("en-IN")}`}
          sub={`${y.hisab_count || 0} HISAB${y.hisab_count === 1 ? "" : "s"}`}
        />
        <MiniStat
          icon={y.shortage_surplus >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          label={t("components.dealerdailybriefing.shortage_surplus")}
          value={`${y.shortage_surplus >= 0 ? "+" : ""}₹${(y.shortage_surplus || 0).toLocaleString("en-IN")}`}
          tone={y.shortage_surplus >= 0 ? "good" : "bad"}
        />
        <MiniStat
          icon={<Sun className="h-4 w-4" />}
          label={t("components.dealerdailybriefing.today_expected")}
          value={fc.sales_expected != null ? `₹${(fc.sales_expected || 0).toLocaleString("en-IN")}` : "—"}
          sub={fc.based_on_weeks ? `${fc.based_on_weeks}wk avg` : "not enough history"}
          delta={delta}
        />
      </div>

      {b.top_anomalies && b.top_anomalies.length > 0 && (
        <div className="relative mt-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Top {b.top_anomalies.length} thing{b.top_anomalies.length === 1 ? "" : "s"} to action today
            </div>
            <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
              <Link to="/dealer/insights" data-testid="briefing-see-all">{t("components.dealerdailybriefing.see_all")} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          <ul className="space-y-2">
            {b.top_anomalies.map((a, i) => (
              <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-background/60 p-2.5 text-sm" data-testid={`briefing-anomaly-${i}`}>
                <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${sevCls(a.severity)}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium leading-tight">{a.detail}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {a.kind} · {a.severity || "LOW"} {a.csa ? `· ${a.csa}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function MiniStat({ icon, label, value, sub, tone, delta }) {
  const toneCls = tone === "bad" ? "text-red-600 dark:text-red-400"
    : tone === "good" ? "text-emerald-600 dark:text-emerald-400"
    : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-background/70 p-3">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </div>
      <div className={`mt-1 text-xl font-bold tabular-nums ${toneCls}`}>{value}</div>
      {(sub || delta != null) && (
        <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
          {delta != null && (
            <span className={`font-semibold ${delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
              {delta >= 0 ? "+" : ""}{delta.toFixed(0)}%
            </span>
          )}
          <span>{sub}</span>
        </div>
      )}
    </div>
  );
}

function sevCls(sev) {
  if (sev === "HIGH") return "bg-red-500/15 text-red-700 dark:text-red-300";
  if (sev === "MED") return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
}
